import React, { useState, useContext } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff, Email, Lock, ArrowRight } from '@mui/icons-material';
import { styled, keyframes } from '@mui/system';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../../context/AuthContext';
import logo from '../../assets/logo.png';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const slideLeft = keyframes`
  from { opacity: 0; transform: translateX(100px); }
  to { opacity: 1; transform: translateX(0); }
`;

const orbitAnimation = keyframes`
  0% { transform: rotate(0deg) translateX(80px) rotate(0deg); }
  100% { transform: rotate(360deg) translateX(80px) rotate(-360deg); }
`;

const orbitAnimation2 = keyframes`
  0% { transform: rotate(0deg) translateX(120px) rotate(0deg); }
  100% { transform: rotate(360deg) translateX(120px) rotate(-360deg); }
`;

const glowPulse = keyframes`
  0%, 100% { opacity: 0.5; }
  50% { opacity: 1; }
`;

const LoadingOverlay = styled(Box)({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: '#ffffff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column',
  gap: '80px',
  zIndex: 99999,
  '@media (max-width: 768px)': {
    gap: '60px',
  },
});

const LoadingCenter = styled(Box)({
  position: 'relative',
  width: '200px',
  height: '200px',
});

const OrbitContainer = styled(Box)({
  position: 'absolute',
  width: '100%',
  height: '100%',
  borderRadius: '50%',
});

const OrbitRing = styled(Box)({
  position: 'absolute',
  width: '100%',
  height: '100%',
  borderRadius: '50%',
  border: '1px solid rgba(16, 185, 129, 0.2)',
});

const Dot = styled(Box)({
  position: 'absolute',
  width: '12px',
  height: '12px',
  borderRadius: '50%',
  background: 'linear-gradient(135deg, #10b981 0%, #06b6d4 100%)',
  boxShadow: '0 0 20px rgba(16, 185, 129, 0.8)',
  top: '-6px',
  left: '50%',
  transform: 'translateX(-50%)',
});

const CenterOrb = styled(Box)({
  position: 'absolute',
  width: '40px',
  height: '40px',
  borderRadius: '50%',
  background: 'radial-gradient(circle, #10b981 0%, #059669 100%)',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  boxShadow: '0 0 40px rgba(16, 185, 129, 0.6), inset 0 0 20px rgba(255, 255, 255, 0.1)',
});

const LoadingText = styled(Typography)({
  fontSize: '24px',
  fontWeight: '700',
  color: '#10b981',
  letterSpacing: '2px',
  animation: `${fadeIn} 0.8s ease`,
});

const MainContainer = styled(Box)({
  minHeight: '100vh',
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  background: '#ffffff',
  '@media (max-width: 1024px)': {
    gridTemplateColumns: '1fr',
  },
  '@media (max-width: 768px)': {
    gridTemplateColumns: '1fr',
  },
});

const LeftPanel = styled(Box)({
  background: 'linear-gradient(135deg, #10b981 0%, #059669 50%, #047857 100%)',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '80px 60px',
  position: 'relative',
  overflow: 'hidden',
  '@media (max-width: 1024px)': {
    display: 'none',
  },
  '@media (max-width: 768px)': {
    display: 'none',
  },
});

const BackgroundShapes = styled(Box)({
  position: 'absolute',
  width: '100%',
  height: '100%',
  top: 0,
  left: 0,
  '&::before': {
    content: '""',
    position: 'absolute',
    width: '400px',
    height: '400px',
    background: 'radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%)',
    borderRadius: '50%',
    top: '-100px',
    right: '-100px',
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    width: '300px',
    height: '300px',
    background: 'radial-gradient(circle, rgba(255, 255, 255, 0.05) 0%, transparent 70%)',
    borderRadius: '50%',
    bottom: '-50px',
    left: '-50px',
  },
});

const LeftContent = styled(Box)({
  position: 'relative',
  zIndex: 10,
  textAlign: 'center',
  maxWidth: '500px',
  animation: `${slideLeft} 0.8s cubic-bezier(0.34, 1.56, 0.64, 1)`,
});

const LeftLogo = styled('img')({
  height: '80px',
  width: 'auto',
  marginBottom: '50px',
  filter: 'brightness(0) invert(1)',
  opacity: 0.95,
  '@media (max-width: 768px)': {
    height: '70px',
  },
});

const LeftTitle = styled(Typography)({
  fontSize: '56px',
  fontWeight: '900',
  color: '#ffffff',
  marginBottom: '30px',
  letterSpacing: '-1px',
  lineHeight: '1.1',
  '@media (max-width: 768px)': {
    fontSize: '48px',
  },
});

const LeftDescription = styled(Typography)({
  fontSize: '18px',
  color: 'rgba(255, 255, 255, 0.85)',
  lineHeight: '1.8',
  fontWeight: '400',
  marginBottom: '50px',
  '@media (max-width: 768px)': {
    fontSize: '16px',
  },
});

const FeatureList = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
  alignItems: 'flex-start',
  '@media (max-width: 768px)': {
    gap: '20px',
  },
});

const Feature = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  animation: `${fadeIn} 0.8s ease`,
  '@media (max-width: 768px)': {
    gap: '12px',
  },
});

const FeatureIcon = styled(Box)({
  width: '48px',
  height: '48px',
  borderRadius: '12px',
  background: 'rgba(255, 255, 255, 0.1)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '24px',
  backdropFilter: 'blur(10px)',
  '@media (max-width: 768px)': {
    width: '40px',
    height: '40px',
    fontSize: '20px',
  },
});

const FeatureText = styled(Typography)({
  fontSize: '15px',
  color: 'rgba(255, 255, 255, 0.9)',
  fontWeight: '500',
  '@media (max-width: 768px)': {
    fontSize: '14px',
  },
});

const RightPanel = styled(Box)({
  background: '#ffffff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '60px 40px',
  '@media (max-width: 768px)': {
    padding: '40px 24px',
    minHeight: '100vh',
  },
  '@media (max-width: 480px)': {
    padding: '30px 20px',
  },
});

const LoginBox = styled(Box)({
  width: '100%',
  maxWidth: '420px',
  animation: `${fadeIn} 0.8s ease 0.2s both`,
  '@media (max-width: 768px)': {
    maxWidth: '380px',
  },
  '@media (max-width: 480px)': {
    maxWidth: '100%',
  },
});

const FormTitle = styled(Typography)({
  fontSize: '48px',
  fontWeight: '900',
  color: '#0f172a',
  marginBottom: '12px',
  letterSpacing: '-1px',
  '@media (max-width: 768px)': {
    fontSize: '42px',
  },
  '@media (max-width: 480px)': {
    fontSize: '36px',
  },
});

const FormDesc = styled(Typography)({
  fontSize: '15px',
  color: '#64748b',
  marginBottom: '50px',
  fontWeight: '500',
  '@media (max-width: 480px)': {
    fontSize: '14px',
    marginBottom: '40px',
  },
});

const InputGroup = styled(Box)({
  marginBottom: '24px',
});

const Label = styled(Typography)({
  fontSize: '12px',
  fontWeight: '700',
  color: '#475569',
  textTransform: 'uppercase',
  letterSpacing: '1px',
  marginBottom: '10px',
  display: 'block',
});

const StyledInput = styled(TextField)({
  width: '100%',
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    backgroundColor: '#f8fafc',
    border: '1.5px solid #e2e8f0',
    fontSize: '15px',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      borderColor: '#cbd5e1',
      backgroundColor: '#ffffff',
    },
    '&.Mui-focused': {
      backgroundColor: '#ffffff',
      borderColor: '#10b981',
      boxShadow: '0 0 0 4px rgba(16, 185, 129, 0.1)',
    },
    '& fieldset': {
      border: 'none',
    },
  },
  '& .MuiOutlinedInput-input': {
    padding: '14px 16px',
    fontWeight: '500',
    color: '#1e293b',
    fontSize: '15px',
    '&::placeholder': {
      color: '#94a3b8',
      opacity: 1,
    },
  },
});

const IconBox = styled(Box)({
  color: '#10b981',
  display: 'flex',
  alignItems: 'center',
});

const ForgotLink = styled(Typography)({
  fontSize: '12px',
  fontWeight: '700',
  color: '#10b981',
  cursor: 'pointer',
  marginBottom: '32px',
  display: 'block',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  transition: 'all 0.2s ease',
  '&:hover': {
    color: '#059669',
    transform: 'translateX(4px)',
  },
});

const SignInBtn = styled(Button)({
  width: '100%',
  padding: '16px 24px',
  borderRadius: '10px',
  fontSize: '15px',
  fontWeight: '800',
  textTransform: 'uppercase',
  letterSpacing: '1px',
  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
  color: '#ffffff',
  border: 'none',
  boxShadow: '0 8px 24px rgba(16, 185, 129, 0.25)',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '10px',
  '&:hover': {
    background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
    transform: 'translateY(-3px)',
    boxShadow: '0 12px 32px rgba(16, 185, 129, 0.35)',
  },
  '&:active': {
    transform: 'translateY(-1px)',
  },
  '&:disabled': {
    background: '#cbd5e1',
    color: '#94a3b8',
    boxShadow: 'none',
  },
});

const AlertBox = styled(Box)(({ severity }) => ({
  marginTop: '20px',
  padding: '14px 16px',
  background: severity === 'error' ? '#fef2f2' : '#f0fdf4',
  border: `1px solid ${severity === 'error' ? '#fecaca' : '#86efac'}`,
  borderRadius: '10px',
  fontSize: '13px',
  color: severity === 'error' ? '#dc2626' : '#16a34a',
  fontWeight: '600',
  animation: `${fadeIn} 0.3s ease`,
}));

export default function AdminLogin() {
  const [credentials, setCredentials] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const { dispatch } = useContext(AuthContext);
  const navigate = useNavigate();

  const handleChange = (e) => {
    setCredentials((prev) => ({ ...prev, [e.target.id]: e.target.value }));
  };

  const handleClick = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const res = await axios.post('https://api.moroccool.com/api/auth/login', credentials);
      if (res.data.isAdmin) {
        // Keep loading screen for 3 seconds
        setTimeout(() => {
          setSuccess(true);
          dispatch({ type: 'LOGIN_SUCCESS', payload: res.data.details });
          setTimeout(() => navigate('/'), 1000);
        }, 1000);
      } else {
        setLoading(false);
        setError('You are not allowed!');
      }
    } catch (err) {
      setLoading(false);
      if (err.response?.status === 404) {
        setError('Admin not found!');
      } else if (err.response?.status === 400) {
        setError('Wrong password or email!');
      } else {
        setError('Something went wrong.');
      }
    }
  };

  return (
    <>
      {loading && (
        <LoadingOverlay>
          <LoadingCenter>
            <OrbitContainer>
              <OrbitRing />
              <OrbitRing sx={{ animation: `${orbitAnimation2} 4s linear infinite` }} />
              
              <Box sx={{ animation: `${orbitAnimation} 6s linear infinite` }}>
                <Dot />
              </Box>
              
              <Box sx={{ animation: `${orbitAnimation} 4s linear infinite reverse` }}>
                <Dot sx={{ background: 'linear-gradient(135deg, #06b6d4 0%, #10b981 100%)' }} />
              </Box>

              <CenterOrb />
            </OrbitContainer>
          </LoadingCenter>
          <LoadingText>Loading Dashboard</LoadingText>
        </LoadingOverlay>
      )}

      <MainContainer>
        <LeftPanel>
          <BackgroundShapes />
          <LeftContent>
            <LeftLogo src={logo} alt="Moroccool" />
           
            <LeftDescription>
              Powerful admin dashboard to manage all your operations seamlessly
            </LeftDescription>
            
            <FeatureList>
              <Feature>
                <FeatureIcon>📊</FeatureIcon>
                <FeatureText>Real-time Analytics</FeatureText>
              </Feature>
              <Feature>
                <FeatureIcon>🏨</FeatureIcon>
                <FeatureText>Manage Hotels & Cafes</FeatureText>
              </Feature>
              <Feature>
                <FeatureIcon>🎉</FeatureIcon>
                <FeatureText>Event Management Tools</FeatureText>
              </Feature>
              <Feature>
                <FeatureIcon>⚙️</FeatureIcon>
                <FeatureText>Full Control & Security</FeatureText>
              </Feature>
            </FeatureList>
          </LeftContent>
        </LeftPanel>

        <RightPanel>
          <LoginBox>
            <FormTitle>Welcome</FormTitle>
            <FormDesc>Sign in to your admin account</FormDesc>

            <Box component="form" onSubmit={(e) => { e.preventDefault(); handleClick(e); }}>
              <InputGroup>
                <Label>Email Address</Label>
                <StyledInput
                  fullWidth
                  id="email"
                  type="email"
                  variant="outlined"
                  placeholder="admin@moroccool.com"
                  value={credentials.email}
                  onChange={handleChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <IconBox>
                          <Email sx={{ fontSize: 20 }} />
                        </IconBox>
                      </InputAdornment>
                    ),
                  }}
                />
              </InputGroup>

              <InputGroup>
                <Label>Password</Label>
                <StyledInput
                  fullWidth
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  variant="outlined"
                  placeholder="••••••••"
                  value={credentials.password}
                  onChange={handleChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <IconBox>
                          <Lock sx={{ fontSize: 20 }} />
                        </IconBox>
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          sx={{
                            color: '#10b981',
                            marginRight: '4px',
                            '&:hover': { color: '#059669' },
                          }}
                        >
                          {showPassword ? <VisibilityOff sx={{ fontSize: 20 }} /> : <Visibility sx={{ fontSize: 20 }} />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </InputGroup>

              <ForgotLink>Forgot password?</ForgotLink>

              <SignInBtn
                disabled={loading}
                onClick={handleClick}
                type="submit"
              >
                {loading ? 'Signing in...' : 'Sign In'}
                {!loading && <ArrowRight sx={{ fontSize: 18 }} />}
              </SignInBtn>

              {error && <AlertBox severity="error">{error}</AlertBox>}
              {success && <AlertBox severity="success">Login successful! Redirecting...</AlertBox>}
            </Box>
          </LoginBox>
        </RightPanel>
      </MainContainer>
    </>
  );
}
